import React, { useState, useEffect } from 'react';
import { MediaMessage, Chat } from '../../../../../chat-api';
import { LittleLoading } from '../../Message';
import {
  MediaRow,
  DocumentContainer,
  DocumentLink,
  DocumentRow,
  ExtentionIconContainer,
  ExtentionIcon,
  DocumentNameContainer,
  DocumentName,
  DownloadIconContainer,
  InfoContainer,
  InfoItemBullet,
  InfoItem, 
} from './styles';

function getFileExtention(message: MediaMessage) {
  const mimetypes: any = {
    'pdf': ['application/pdf'],
    'xls': ['application/vnd.ms-excel'], 
    'xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'], 
    'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  };
  const extention = message.body.file.name && message.body.file.name!.split('.').pop();

  if (extention && mimetypes[extention]) {
    return extention;
  }

  if (mimetypes['pdf'].includes(message.body.file.contentType!)) {
    return 'pdf';
  } else if (mimetypes['xls'].includes(message.body.file.contentType!)) {
    return 'xls';
  } else if (mimetypes['xlsx'].includes(message.body.file.contentType!)) {
    return 'xlsx';
  } else if (mimetypes['docx'].includes(message.body.file.contentType!)) {
    return 'docx';
  } else {
    return 'others';
  }
}

function getFileSize(message: MediaMessage) {
  const size = message.body.file.length!;

  if (size < 1000) {
    return `${size} B`;
  } else if (Math.round(size / 1000) < 1000) {
    return `${Math.round(size / 1000)} kB`;
  } else if (Math.round((size / 1000) / 1000) < 1000) {
    return `${Math.round((size / 1000) / 1000)} MB`;
  } else {
    return `${Math.round(((size / 1000) / 1000) / 1000)} GB`;
  }
}

interface MessageBodyDocumentProps {
  message: MediaMessage,
  mediaDocument:HTMLAnchorElement | null,
  mediaBlob: Blob | null,
  chat: Chat | null,
  textColor?: string;
  backgroundColor?: string;
}

function MessageBodyDocument({ message, mediaDocument, mediaBlob, chat, textColor, backgroundColor }: MessageBodyDocumentProps) {
  const [numberOfPages, setNumberOfPages] = useState(0);
  const getImagePath = (fileName: string) => require(`../../../../../assets/images/extentions/${fileName}.png`);
  
  useEffect(() => {
    function getNumberOfPages() {
      var reader = new FileReader();
      if (mediaBlob) {
        reader.readAsBinaryString(mediaBlob);
        reader.onloadend = function () {
          var count = reader.result?.toString().match(/\/Type[\s]*\/Page[^s]/g)?.length || 0;
          setNumberOfPages(count);
        }
      }
    }

    if (getFileExtention(message) === 'pdf') {
      getNumberOfPages();
    }
  }, [message, mediaBlob]);

  const handleClick = () => {
    if (message.body.file && mediaBlob) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(mediaBlob);
      a.download = message.body.file.name!;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }
  
  return (
    <sdiv>
      <DocumentContainer as="sdiv">
        <MediaRow as="sdiv">
          <DocumentLink as="sdiv" onClick={handleClick} title={`Download "${message.body.filename}"`}>
            <DocumentRow as="sdiv" style={{backgroundColor: backgroundColor}}>
              <ExtentionIconContainer as="sdiv">
                <ExtentionIcon as="sdiv" style={{backgroundImage: `url(${getImagePath(getFileExtention(message))})`}}></ExtentionIcon>
              </ExtentionIconContainer>
              <DocumentNameContainer as="sdiv">
                <DocumentName as="sdiv" dir="auto">{message.body.filename || message.body.file.name}</DocumentName>
              </DocumentNameContainer>
              <DownloadIconContainer as="sdiv">
                {(!mediaDocument || !mediaDocument.href) ? 
                  <LittleLoading message={message}/> 
                :
                  <sdiv data-icon="document-download" style={{ height: '34px', cursor: 'pointer' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34 34" width="34" height="34">
                      <path fill="currentColor" d="M17 2c8.3 0 15 6.7 15 15s-6.7 15-15 15S2 25.3 2 17 8.7 2 17 2m0-1C8.2 1 1 8.2 1 17s7.2 16 16 16 16-7.2 16-16S25.8 1 17 1z"></path>
                      <path fill="currentColor" d="M22.4 17.5h-3.2v-6.8c0-.4-.3-.7-.7-.7h-3.2c-.4 0-.7.3-.7.7v6.8h-3.2c-.6 0-.8.4-.4.8l5 5.3c.5.7 1 .5 1.5 0l5-5.3c.7-.5.5-.8-.1-.8z"></path>
                    </svg>
                  </sdiv>
                }
              </DownloadIconContainer>
            </DocumentRow>
          </DocumentLink>
        </MediaRow>
      </DocumentContainer>
      <InfoContainer as="sdiv" textColor={textColor}>
        {!!numberOfPages && (
          <>
            <InfoItem>
              {`${numberOfPages} ${numberOfPages > 1 ? 'páginas' : 'página'}`}
            </InfoItem>
            <InfoItemBullet>&bull;</InfoItemBullet>
          </>
        )}
        <InfoItem data-meta-key="type" title={getFileExtention(message)}>
          {getFileExtention(message) === 'others' ? 
            message.body.file.name?.substring(message.body.file.name?.lastIndexOf('.')+1, message.body.file.name?.length).toUpperCase()
          : getFileExtention(message).toUpperCase()
          }
        </InfoItem>
        <InfoItemBullet>&bull;</InfoItemBullet>
        <InfoItem title={getFileSize(message)}>{getFileSize(message)}</InfoItem>
      </InfoContainer>
    </sdiv>
  );
}

export default React.memo(MessageBodyDocument);